import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Drawer, Form, Input, Select, Space, Spin, Switch, Tag, Typography } from 'antd';
import { CopyOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { apiUrl, getApiErrorMessage } from '@api/server';
import {
  CHANNEL_TYPE_LABELS,
  createChannel,
  getChannel,
  getChannelRuntimeStatusColor,
  getChannelRuntimeStatusLabel,
  updateChannel,
} from './channels';

const { Paragraph, Text } = Typography;

const CHANNEL_TYPE_OPTIONS = Object.entries(CHANNEL_TYPE_LABELS).map(([value, label]) => ({
  value: Number(value),
  label,
}));

const EXTERNAL_CHANNEL_VID = 6;
const CHAT_CHANNEL_VID = 7;
const AUTO_TOKEN_VIDS = new Set([EXTERNAL_CHANNEL_VID, CHAT_CHANNEL_VID]);

const CODE_EXAMPLES = {
  1: {
    placeholder: 'sip:000202678:password@voice.telphin.com:5068',
    extra: 'SIP: sip:логин:пароль@домен:порт',
  },
  2: {
    placeholder: 'MAX API key',
    extra: 'MAX: вставьте API key бота целиком.',
  },
  3: {
    placeholder: 'Можно оставить пустым',
    extra: 'Виджет: code обычно не требуется.',
  },
  4: {
    placeholder: 'Group Id:Confirmation code:Access token',
    extra: 'VK: <Group Id>:<Confirmation code>:<Access token целиком>.',
  },
  5: {
    placeholder: 'company.bitrix24.ru/rest/1/xxxxx',
    extra: 'Bitrix24: REST base (без https://), например company.bitrix24.ru/rest/1/xxxxx. Можно оставить пустым — возьмётся из config.',
  },
  6: {
    placeholder: 'Сгенерируется при сохранении',
    extra: 'External: токен для заголовка token в POST /callback/external. Генерируется автоматически, менять нельзя.',
  },
  7: {
    placeholder: 'Сгенерируется при сохранении',
    extra: 'Чат: токен для виджета. Генерируется автоматически, менять нельзя.',
  },
};

const getWebPublicUrl = () => (
  (process.env.REACT_APP_WEB_PUBLIC_URL || (typeof window !== 'undefined' ? window.location.origin : ''))
    .replace(/\/$/, '')
);

const buildChatSnippet = (tokenCode) => {
  const webPublicUrl = getWebPublicUrl();
  const apiPublicUrl = (apiUrl || '').replace(/\/$/, '');
  const params = new URLSearchParams({
    tokenCode,
    apiBase: apiPublicUrl,
  });
  return `<script src="${webPublicUrl}/chat.js?${params.toString()}"></script>`;
};

const Channel = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [runtimeStatus, setRuntimeStatus] = useState('');
  const [copyHint, setCopyHint] = useState('');

  const channelId = useMemo(() => {
    if (!id) {
      return null;
    }

    const nextId = Number(id);
    return Number.isInteger(nextId) && nextId > 0 ? nextId : null;
  }, [id]);
  const isNewChannel = !id;
  const title = isNewChannel ? 'Новый канал' : `Канал #${channelId || ''}`;
  const selectedVid = Form.useWatch('vid', form);
  const savedCode = Form.useWatch('code', form);
  const isAutoTokenChannel = AUTO_TOKEN_VIDS.has(Number(selectedVid));
  const isChatChannel = Number(selectedVid) === CHAT_CHANNEL_VID;
  const codeExample = CODE_EXAMPLES[selectedVid] || CODE_EXAMPLES[1];
  const chatSnippet = isChatChannel && savedCode ? buildChatSnippet(savedCode) : '';
  const closeDrawer = useCallback(() => {
    navigate('/channels');
  }, [navigate]);

  const loadChannel = useCallback(async () => {
    if (isNewChannel) {
      form.setFieldsValue({
        name: '',
        vid: 1,
        code: '',
        active: false,
      });
      setRuntimeStatus('deactivated');
      return;
    }

    if (!channelId) {
      setError('Некорректный идентификатор канала.');
      return;
    }

    setError('');
    setIsLoading(true);
    try {
      const channel = await getChannel(channelId);
      form.setFieldsValue({
        name: channel.name,
        vid: Number(channel.vid),
        code: channel.code || '',
        active: Boolean(channel.active),
      });
      setRuntimeStatus(channel.runtimeStatus || '');
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setIsLoading(false);
    }
  }, [channelId, form, isNewChannel]);

  useEffect(() => {
    loadChannel();
  }, [loadChannel]);

  const handleSave = async (values) => {
    setError('');
    setIsSaving(true);
    try {
      const isAutoToken = AUTO_TOKEN_VIDS.has(Number(values.vid));
      const payload = {
        ...values,
        code: isAutoToken ? undefined : (values.code?.trim() || null),
      };
      if (isAutoToken) {
        delete payload.code;
      }

      if (isNewChannel) {
        const channel = await createChannel(payload);
        navigate(`/channels/${channel.id}`);
        return;
      }

      await updateChannel({
        id: channelId,
        ...payload,
      });
      if (Number(values.vid) === CHAT_CHANNEL_VID) {
        await loadChannel();
        return;
      }
      navigate('/channels');
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopySnippet = async () => {
    if (!chatSnippet) {
      return;
    }
    try {
      await navigator.clipboard.writeText(chatSnippet);
      setCopyHint('Скопировано');
      setTimeout(() => setCopyHint(''), 2000);
    } catch {
      setCopyHint('Не удалось скопировать');
    }
  };

  return (
    <Drawer
      className="user-drawer channel-drawer"
      title={title}
      placement="right"
      width={560}
      open
      onClose={closeDrawer}
      destroyOnHidden
      footer={(
        <div className="user-drawer-footer">
          <Button type="primary" icon={<SaveOutlined />} loading={isSaving} onClick={() => form.submit()}>
            Сохранить
          </Button>
          <Button onClick={closeDrawer}>Отмена</Button>
        </div>
      )}
    >
      {error && (
        <Alert className="user-alert" type="error" message={error} showIcon />
      )}

      <Spin spinning={isLoading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          requiredMark={false}
          initialValues={{
            name: '',
            vid: 1,
            code: '',
            active: false,
          }}
        >
          <Form.Item
            label="Название"
            name="name"
            rules={[
              { required: true, message: 'Введите название канала.' },
              { max: 80, message: 'Название должно быть не длиннее 80 символов.' },
            ]}
          >
            <Input placeholder="Название канала" autoFocus />
          </Form.Item>

          <Form.Item
            label="Тип"
            name="vid"
            rules={[{ required: true, message: 'Выберите тип канала.' }]}
          >
            <Select options={CHANNEL_TYPE_OPTIONS} />
          </Form.Item>

          <Form.Item label="Code" name="code" extra={codeExample.extra}>
            <Input
              placeholder={codeExample.placeholder}
              readOnly={isAutoTokenChannel}
              disabled={isAutoTokenChannel && isNewChannel}
            />
          </Form.Item>

          {chatSnippet && (
            <Form.Item label="Код для приложения">
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Paragraph className="channel-embed-snippet" copyable={false}>
                  <Text code>{chatSnippet}</Text>
                </Paragraph>
                <Space>
                  <Button icon={<CopyOutlined />} onClick={handleCopySnippet}>
                    Копировать
                  </Button>
                  {copyHint && <Text type="secondary">{copyHint}</Text>}
                </Space>
              </Space>
            </Form.Item>
          )}

          <div className="form-row">
            <Form.Item label="Состояние">
              <Tag color={getChannelRuntimeStatusColor(runtimeStatus)}>
                {getChannelRuntimeStatusLabel(runtimeStatus)}
              </Tag>
            </Form.Item>

            <Form.Item label="Активен" name="active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
        </Form>
      </Spin>
    </Drawer>
  );
};

export default Channel;
